import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

// Figma node 89:26972 — Bihar Krishi Web Handoff (the "Recent Searches" state of
// the global-search dropdown). Reuses the exact detached, bottom-rounded Paper
// chrome as GlobalSearchResultBox so the history and results dropdowns are
// visually identical; only the body differs: clock + past query + per-item
// delete. All colours/typography/spacing route through the central rewamp theme
// — no hex, no hardcoded font-family.


export const RecentSearchesBox = ({
  open,
  anchorEl,
  recentSearches,
  loading,
  onSelect,
  onDeleteOne,
  onClearAll,
  sx,
}) => {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label={t("REWAMP_RECENT_SEARCHES")}
      sx={{
        position: "absolute",
        // Mirror GlobalSearchResultBox: 8px below the input, detached box with
        // full border + bottom radius, width tracking the (capped) input.
        top: anchorEl ? anchorEl.offsetHeight + 8 : 46,
        right: 0,
        width: anchorEl ? anchorEl.offsetWidth : "100%",
        bgcolor: "background.paper",
        border: "0.8px solid",
        borderColor: "divider",
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
        overflow: "hidden",
        zIndex: 1300,
        maxHeight: "450px",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Box sx={{ overflowY: "auto", flexGrow: 1, p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <Typography
            component="h3"
            variant="overline"
            sx={{
              color: "text.primary",
              fontSize: "0.75rem",
              letterSpacing: "0.06em",
              lineHeight: 1.4,
            }}
          >
            {t("REWAMP_RECENT_SEARCHES")}
          </Typography>
          {recentSearches.length > 0 && (
            // Same Clear-All affordance as the results box first header (native
            // button so the MUI Button theme overrides don't enlarge/bolden it);
            // wired to the backend delete-all endpoint.
            <Box
              component="button"
              type="button"
              onClick={onClearAll}
              sx={{
                border: "none",
                background: "none",
                cursor: "pointer",
                p: 0,
                typography: "overline",
                fontWeight: 400,
                fontSize: "0.7rem",
                letterSpacing: "0.06em",
                color: "text.primary",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {t("ClearAll")}
            </Box>
          )}
        </Box>

        {loading && recentSearches.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={20} sx={{ color: "primary.interactive" }} />
          </Box>
        ) : (
          <Box
            component="ul"
            sx={{ listStyle: "none", p: 0, m: 0, pb: 1, display: "flex", flexDirection: "column" }}
          >
            {recentSearches.map((item) => (
              <Box
                component="li"
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 1,
                  "&:hover": { bgcolor: "primary.softSurface" },
                }}
              >
                <AccessTimeFilledIcon
                  aria-hidden="true"
                  sx={{ fontSize: 20, color: "accent.butterscotch", flexShrink: 0 }}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => onSelect(item.searchedText)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    background: "none",
                    p: 0,
                    m: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    font: "inherit",
                  }}
                >
                  <Typography
                    variant="body1"
                    noWrap sx={{ color: "text.primary", fontWeight: 500}}
                  >
                    {item.searchedText}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`${t("REWAMP_RECENT_SEARCH_REMOVE")}: ${item.searchedText}`}
                  onClick={() => onDeleteOne(item.id)}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    borderRadius: "6px",
                    bgcolor: "primary.main",
                    color: "background.white",
                    flexShrink: 0,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default RecentSearchesBox;
